import { produce } from 'immer';
import { create } from 'zustand';
import { customerIntelligenceDataStore } from './customer-intelligence-data';
import { storeFilters } from '../../saved-filters/store';
import { GetWorkspaceCrmIntegrationResponse } from '../../../services/types';
import { getFilters } from '../../saved-filters/get';
import { setCurrentView } from '../../saved-filters/set-current-view';
import { removeCurrentView } from '../../saved-filters/remove-current-view';
import { SavedFiltersFilterItem } from '../../saved-filters/types';
import { ParticipantFilter } from '../../../API/types';

export const INSIGHTS_PAGE_KEY = 'insights'

export interface Member {
  id: string;
  fullName: string;
  selected: boolean;
}
export interface Industry {
  id: string;
}
export interface Priority {
  id: string;
}
export interface LifecycleStage {
  id: string;
}

export type Selectable<T extends object> = T & { selected: boolean };

interface CustomerIntelligenceFiltersStore {
  category: string;
  dateTypeOption: string;
  dateRange: [null, null] | [Date, Date];
  members: Member[];
  meetingsImInvitedOnly: boolean;
  accounts: {
    id: string;
    name: string;
    provider: string;
  }[];
  industries: Selectable<Industry>[];
  lifecycleStages: Selectable<LifecycleStage>[];
  priorities: Selectable<Priority>[];
  contractRenewalDateBefore: Date | null;
  contractValueMin: number | null;
  contractValueMax: number | null;
  group: string | null;
  subGroup: string | null;
  participants: ParticipantFilter[];
  crmFilters: Map<string, string[]>;
  currentFilterView: string;
}

const customerIntelligenceFiltersStore = create<CustomerIntelligenceFiltersStore>(() => {
  return {
    category: 'Risks',
    dateTypeOption: '30d',
    dateRange: [null, null],
    members: [],
    meetingsImInvitedOnly: false,
    accounts: [],
    industries: [],
    priorities: [],
    lifecycleStages: [],
    contractRenewalDateBefore: null,
    contractValueMax: null,
    contractValueMin: null,
    group: null,
    subGroup: null,
    participants: [],
    crmFilters: new Map(),
    currentFilterView: '',
  };
});

const saveFilterView = (viewName: string, crmProperties: GetWorkspaceCrmIntegrationResponse['workspace']['properties']) => {
  const state = customerIntelligenceFiltersStore.getState();

  const filters: Record<string, SavedFiltersFilterItem> = {
    accounts: {
      label: 'Accounts',
      type: 'accountsJson',
      value: JSON.stringify(state.accounts)
    },
    dateRange: {
      label: 'Date Range',
      type: 'dateRange',
      value: state.dateRange
    },
    dateTypeOption: {
      label: 'Date Range Type',
      type: 'dateRangeType',
      value: state.dateTypeOption
    },
    category: {
      label: 'Tracker Type',
      type: 'text',
      value: state.category
    }
  }

  const crmFilters = Array.from(state.crmFilters)
    .reduce((acc, [key, value]) => {
      const crmProperty = crmProperties.find(p => p.id === key);
      acc[`crm_${key}`] = {
        label: crmProperty?.label || key,
        type: `crm_${crmProperty?.type || key}`,
        value: value
      }
      return acc;
    }, {} as Record<string, SavedFiltersFilterItem>)

  const allFilters = {
    ...filters,
    ...crmFilters
  }

  storeFilters({ pageKey: INSIGHTS_PAGE_KEY, pageTitle: 'VoC Topic Tracking', viewName, filters: allFilters });
};

const setCurrentFilterView = (viewId: string | null) => {
  const state = customerIntelligenceFiltersStore.getState();

  if (!viewId) {
    removeCurrentView(INSIGHTS_PAGE_KEY);
    customerIntelligenceFiltersStore.setState({
      ...state,
      category: 'Risks',
      dateTypeOption: '30d',
      dateRange: [null, null],
      accounts: [],
      crmFilters: new Map(),
      currentFilterView: '',
    })
    return;
  }

  const filters = getFilters({ pageKey: INSIGHTS_PAGE_KEY, viewId });
  setCurrentView(INSIGHTS_PAGE_KEY, viewId);

  const newFilters = {
    category: filters.category as string,
    dateTypeOption: filters.dateTypeOption as string,
    dateRange: (filters.dateRange as [string, string]).map((date: string) => new Date(date)) as [Date, Date] | [null, null],
    accounts: JSON.parse(filters.accounts as string),
    crmFilters: Object.entries(filters).filter(([key]) => key.startsWith('crm_')).reduce((acc, [key, value]) => {
      acc.set(key.replace('crm_', ''), value as string[]);
      return acc;
    }, new Map<string, string[]>()),
    currentFilterView: viewId,
  }

  customerIntelligenceFiltersStore.setState({
    ...state,
    ...newFilters
  })
}

const meetingsImInvitedOnlyToggled = (value: boolean) => {
  customerIntelligenceFiltersStore.setState(() => ({ meetingsImInvitedOnly: value }));
};

const categoryChanged = (category: string) => {
  customerIntelligenceFiltersStore.setState(() => ({ category }));
}

const setCrmMap = (crmFilters: Map<string, string[]>) => {
  customerIntelligenceFiltersStore.setState(() => ({ crmFilters }));
}

const dateTypeOptionChanged = (dateTypeOption: string) => {
  customerIntelligenceFiltersStore.setState(() => ({ dateTypeOption }));
}

const dateRangeChanged = (dateRange: [Date, Date] | [null, null]) => {
  customerIntelligenceFiltersStore.setState(() => ({ dateRange }));
}

const memberSelectionToggled = (memberId: string) => {
  customerIntelligenceFiltersStore.setState(state =>
    produce(state, draft => {
      draft.members = draft.members.map(member => {
        if (member.id === memberId) {
          member.selected = !member.selected;
        }
        return member;
      });
    }),
  );
};

const allMembersToggled = (selected: boolean) => {
  customerIntelligenceFiltersStore.setState(state =>
    produce(state, draft => {
      draft.members = draft.members.map(member => {
        member.selected = selected;
        return member;
      });
    }),
  );
};

const accountsSelected = (accounts: { id: string; provider: string; name: string }[]) => {
  customerIntelligenceFiltersStore.setState(() => ({ accounts }));
};

const groupSelected = (group: string) => {
  customerIntelligenceFiltersStore.setState(() => ({ group }));
};

const contractRenewalDateBeforeChanged = (contractRenewalDateBefore: Date | null) => {
  customerIntelligenceFiltersStore.setState(() => ({ contractRenewalDateBefore }));
};

const contractValuesChanged = (minValue: number | null, maxValue: number | null) => {
  customerIntelligenceFiltersStore.setState(() => ({
    contractValueMin: minValue,
    contractValueMax: maxValue,
  }));
};

const participantsChanged = (participants: ParticipantFilter[]) => {
  customerIntelligenceFiltersStore.setState(() => ({ participants }));
};

const filtersLoaded = (
  industries: string[],
  lifecycleStages: string[],
  priorities: string[],
  members: { id: string; fullName: string }[],
) => {
  const {
    lifecycleStages: lsStore,
    priorities: pStore,
    members: mStore,
    industries: iStore,
  } = customerIntelligenceFiltersStore.getState();

  const mappedIndustries = ['(No value set)', ...industries].map(id => {
    const exists = iStore.find(l => l.id === id);
    return {
      id,
      selected: exists ? exists.selected : true,
    };
  });

  const mappedLifcycle = ['(No value set)', ...lifecycleStages].map(id => {
    const exists = lsStore.find(l => l.id === id);
    return {
      id,
      selected: exists ? exists.selected : true,
    };
  });

  const mappedPriorities = ['(No value set)', ...priorities].map(id => {
    const exists = pStore.find(l => l.id === id);
    return {
      id,
      selected: exists ? exists.selected : true,
    };
  });

  const mappedMembers = members.map(mem => {
    const exists = mStore.find(l => l.id === mem.id);
    return {
      id: mem.id,
      fullName: mem.fullName,
      selected: exists ? exists.selected : true,
    };
  });

  customerIntelligenceFiltersStore.setState(() => ({
    industries: mappedIndustries,
    lifecycleStages: mappedLifcycle,
    priorities: mappedPriorities,
    members: mappedMembers,
  }));
};

const filtersReset = () => {
  const {
    lifecycleStages: lsStore,
    priorities: pStore,
    members: mStore,
    industries: iStore,
  } = customerIntelligenceFiltersStore.getState();

  const { refetchData } = customerIntelligenceDataStore.getState();

  customerIntelligenceFiltersStore.setState(() => ({
    members: mStore.map(ls => ({ ...ls, selected: true })),
    meetingsImInvitedOnly: false,
    dateTypeOption: '30d',
    dateRange: [null, null],
    accounts: [],
    industries: iStore.map(ls => ({ ...ls, selected: true })),
    lifecycleStages: lsStore.map(ls => ({ ...ls, selected: true })),
    priorities: pStore.map(ls => ({ ...ls, selected: true })),
    contractRenewalDateBefore: null,
    contractValueMin: null,
    contractValueMax: null,
    crmFilters: new Map(),
  }));

  refetchData();
};

const reducers = {
  accountsSelected,
  categoryChanged,
  dateTypeOptionChanged,
  dateRangeChanged,
  groupSelected,
  contractRenewalDateBeforeChanged,
  contractValuesChanged,
  filtersLoaded,
  meetingsImInvitedOnlyToggled,
  allMembersToggled,
  filtersReset,
  memberSelectionToggled,
  setCrmMap,
  saveFilterView,
  setCurrentFilterView,
  participantsChanged,
};

export { reducers, customerIntelligenceFiltersStore, CustomerIntelligenceFiltersStore };